import { useState, useEffect, useCallback, useRef } from "react";
import { ProductConfiguratorApiService } from "api/productConfigurator.api";

/**
 * Evaluate a single condition against the current selections.
 * Condition format: { field: "option_{id}", operator: "eq"|"neq"|"gt"|"gte"|"lt"|"lte"|"contains", value: "..." }
 *
 * "field" is matched against selected option IDs or option values.
 * - "option_{id}" checks if that specific option is currently selected in any group.
 */
const evaluateCondition = (cond, selectedOptions, optionsMap) => {
  const { field, operator, value } = cond;
  if (!field) return false;

  // Extract option ID from field like "option_5"
  const optionIdMatch = field.match(/^option_(\d+)$/);
  if (!optionIdMatch) return false;

  const conditionOptionId = parseInt(optionIdMatch[1], 10);
  const selectedIds = Object.values(selectedOptions).map(Number);
  const isSelected = selectedIds.includes(conditionOptionId);

  // For select-based options, "value" in the condition is often empty or ignored.
  // The primary check is whether the option is selected or not.
  // If value is provided, we compare it against the option's own value/name.

  if (!value && value !== "0") {
    // No value specified — just check selection state
    switch (operator) {
      case "eq":
        return isSelected;
      case "neq":
        return !isSelected;
      default:
        return isSelected;
    }
  }

  // Value-based comparison: find the option and compare
  let optionObj = null;
  for (const grpId in optionsMap) {
    const found = optionsMap[grpId].find((o) => o.id === conditionOptionId);
    if (found) {
      optionObj = found;
      break;
    }
  }

  if (!optionObj) return false;

  // If the option isn't selected, condition doesn't apply
  if (!isSelected) {
    return operator === "neq";
  }

  // Compare against the condition value
  const optVal = String(optionObj.value ?? optionObj.name ?? "").toLowerCase();
  const condVal = String(value).toLowerCase();
  const numOpt = parseFloat(optVal);
  const numCond = parseFloat(condVal);
  const bothNumeric = !isNaN(numOpt) && !isNaN(numCond);

  switch (operator) {
    case "eq":
      return bothNumeric ? numOpt === numCond : optVal === condVal;
    case "neq":
      return bothNumeric ? numOpt !== numCond : optVal !== condVal;
    case "gt":
      return bothNumeric && numOpt > numCond;
    case "gte":
      return bothNumeric && numOpt >= numCond;
    case "lt":
      return bothNumeric && numOpt < numCond;
    case "lte":
      return bothNumeric && numOpt <= numCond;
    case "contains":
      return optVal.includes(condVal);
    default:
      return false;
  }
};

const useConfiguratorRuleEngine = (templateId, restoredSelections = null) => {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [optionGroups, setOptionGroups] = useState([]);
  const [options, setOptions] = useState({}); // Map: groupId -> [options]
  const [rules, setRules] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);

  // State
  const [selectedOptions, setSelectedOptions] = useState({}); // Map: groupId -> optionId
  const [visibleOptions, setVisibleOptions] = useState(new Set()); // Set of optionIds
  const [disabledOptions, setDisabledOptions] = useState(new Set()); // Set of optionIds
  const [price, setPrice] = useState({ base: 0, adjustments: 0, total: 0 });

  // Ref to prevent infinite loops during auto-correction
  const isAutoCorrecting = useRef(false);

  // 1. Fetch Data
  useEffect(() => {
    if (!templateId) {
      setTemplate(null);
      setOptionGroups([]);
      setOptions({});
      setRules([]);
      setPricingRules([]);
      setSelectedOptions({});
      setVisibleOptions(new Set());
      setDisabledOptions(new Set());
      setPrice({ base: 0, adjustments: 0, total: 0 });
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [templateRes, groupsRes, rulesRes, pricingRulesRes] = await Promise.all([
          ProductConfiguratorApiService.getTemplate(templateId),
          ProductConfiguratorApiService.getOptionGroups(templateId),
          ProductConfiguratorApiService.getRules(templateId),
          ProductConfiguratorApiService.getPricingRules(templateId),
        ]);

        const tmpl = templateRes.data.data || templateRes.data;
        const groups = groupsRes.data.data;

        setTemplate(tmpl);
        setOptionGroups(groups);
        setRules(rulesRes.data.data || []);
        setPricingRules(pricingRulesRes.data.data || []);

        // Fetch options for each group
        const optionsMap = {};
        await Promise.all(
          groups.map(async (group) => {
            const res = await ProductConfiguratorApiService.getOptions(group.id);
            optionsMap[group.id] = res.data.data || [];
          })
        );
        setOptions(optionsMap);

        // Initial Selection — use restored selections if provided, otherwise defaults
        if (restoredSelections && Object.keys(restoredSelections).length > 0) {
          // Validate restored selections still exist in the options
          const validated = {};
          Object.entries(restoredSelections).forEach(([groupId, optionId]) => {
            const grpOpts = optionsMap[groupId] || [];
            const exists = grpOpts.find((o) => o.id === Number(optionId) && o.isActive);
            if (exists) {
              validated[groupId] = Number(optionId);
            } else {
              // Fall back to default for this group
              const defaultOpt = grpOpts.find((o) => o.isDefault && o.isActive) || grpOpts.find((o) => o.isActive);
              if (defaultOpt) validated[groupId] = defaultOpt.id;
            }
          });
          // Also fill in any groups not in restoredSelections
          Object.entries(optionsMap).forEach(([groupId, groupOptions]) => {
            if (!validated[groupId]) {
              const defaultOpt = groupOptions.find((o) => o.isDefault && o.isActive) || groupOptions.find((o) => o.isActive);
              if (defaultOpt) validated[groupId] = defaultOpt.id;
            }
          });
          setSelectedOptions(validated);
        } else {
          const initialSelections = {};
          Object.entries(optionsMap).forEach(([groupId, groupOptions]) => {
            let selectedOpt = groupOptions.find((o) => o.isDefault && o.isActive);
            if (!selectedOpt) {
              selectedOpt = groupOptions.find((o) => o.isActive);
            }
            if (selectedOpt) {
              initialSelections[groupId] = selectedOpt.id;
            }
          });
          setSelectedOptions(initialSelections);
        }
      } catch (error) {
        console.error("Failed to load configurator data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [templateId]);

  // 2. Evaluate Rules — Show/Hide/Enable/Disable logic
  const evaluateRules = useCallback(() => {
    if (!template) return;

    const newVisible = new Set();
    const newDisabled = new Set();

    // Initialize all active options as visible
    Object.values(options)
      .flat()
      .forEach((opt) => {
        if (opt.isActive) newVisible.add(opt.id);
      });

    // Sort rules by priority and evaluate
    const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

    console.log("[RuleEngine] Evaluating", sortedRules.length, "rules");
    console.log("[RuleEngine] Selected options:", selectedOptions);
    console.log("[RuleEngine] Options map keys:", Object.keys(options));

    sortedRules.forEach((rule) => {
      if (!rule.isActive) return;

      let conditionMet = false;
      const selectedIds = Object.values(selectedOptions).map(Number);

      // Check conditionOptionId first (Rules model uses this)
      if (rule.conditionOptionId) {
        const condOptId = Number(rule.conditionOptionId);
        const op = (rule.conditionOperator || "eq").toLowerCase();
        if (op === "eq" || op === "equals") {
          conditionMet = selectedIds.includes(condOptId);
        } else if (op === "neq" || op === "notequals") {
          conditionMet = !selectedIds.includes(condOptId);
        } else if (op === "in") {
          conditionMet = selectedIds.includes(condOptId);
        } else if (op === "notin") {
          conditionMet = !selectedIds.includes(condOptId);
        }
      } else if (rule.conditionJson) {
        // JSON-based conditions (Pricing Rules model uses this)
        try {
          let conditions = rule.conditionJson;
          if (typeof conditions === "string") {
            conditions = JSON.parse(conditions);
          }
          if (Array.isArray(conditions) && conditions.length > 0) {
            conditionMet = conditions.every((cond) =>
              evaluateCondition(cond, selectedOptions, options)
            );
          }
        } catch (e) {
          console.error("Invalid condition JSON", e);
        }
      }

      console.log(`[RuleEngine] Rule "${rule.name}": conditionOptionId=${rule.conditionOptionId}, operator=${rule.conditionOperator}, conditionMet=${conditionMet}, action=${rule.action}, targetOptionId=${rule.targetOptionId}, targetOptionGroupId=${rule.targetOptionGroupId}`);

      if (conditionMet) {
        // Apply action to target option(s)
        const targetIds = [];

        if (rule.targetOptionId) {
          targetIds.push(Number(rule.targetOptionId));
        }

        // If targeting a group, apply to all options in that group
        if (rule.targetOptionGroupId || rule.targetGroupId) {
          const groupId = String(rule.targetOptionGroupId || rule.targetGroupId);
          // options map keys may be string or number — try both
          const groupOpts = options[groupId] || options[Number(groupId)] || [];
          groupOpts.forEach((opt) => targetIds.push(opt.id));
        }

        targetIds.forEach((targetId) => {
          const action = (rule.action || "").toLowerCase();
          if (action === "show") {
            newVisible.add(targetId);
          } else if (action === "hide") {
            newVisible.delete(targetId);
          } else if (action === "enable") {
            newDisabled.delete(targetId);
          } else if (action === "disable") {
            newDisabled.add(targetId);
          } else if (action === "require") {
            // Require = make visible + not disabled
            newVisible.add(targetId);
            newDisabled.delete(targetId);
          }
        });
      }
    });

    setVisibleOptions(newVisible);
    setDisabledOptions(newDisabled);

    // 3. Auto-correct selections — if a selected option is now hidden or disabled,
    //    switch to the first available (visible + enabled) option in that group.
    if (!isAutoCorrecting.current) {
      const correctedSelections = { ...selectedOptions };
      let needsCorrection = false;

      Object.entries(correctedSelections).forEach(([groupId, optionId]) => {
        const isHidden = !newVisible.has(optionId);
        const isDisabledOpt = newDisabled.has(optionId);

        if (isHidden || isDisabledOpt) {
          // Find first visible + enabled option in this group
          const groupOpts = (options[groupId] || []).filter(
            (opt) => opt.isActive && newVisible.has(opt.id) && !newDisabled.has(opt.id)
          );

          if (groupOpts.length > 0) {
            // Pick default first, or first available
            const defaultOpt = groupOpts.find((o) => o.isDefault) || groupOpts[0];
            correctedSelections[groupId] = defaultOpt.id;
            needsCorrection = true;
          } else {
            // No options available — clear selection
            delete correctedSelections[groupId];
            needsCorrection = true;
          }
        }
      });

      if (needsCorrection) {
        isAutoCorrecting.current = true;
        setSelectedOptions(correctedSelections);
        // Reset flag after state update processes
        setTimeout(() => {
          isAutoCorrecting.current = false;
        }, 0);
      }
    }
  }, [template, options, rules, selectedOptions]);

  // 3. Calculate Price
  const calculatePrice = useCallback(() => {
    if (!template) return;

    let totalAdjustments = 0;
    const base = template.basePrice || 0;

    // Option Adjustments (only for visible, non-disabled selections)
    Object.entries(selectedOptions).forEach(([groupId, optionId]) => {
      const grpOptions = options[groupId] || [];
      const option = grpOptions.find((o) => o.id === Number(optionId));

      if (option && option.priceAdjustment) {
        // Only count if visible and not disabled
        if (visibleOptions.has(option.id) || visibleOptions.size === 0) {
          if (option.priceAdjustmentType === "Percentage") {
            totalAdjustments += base * (option.priceAdjustment / 100);
          } else {
            totalAdjustments += option.priceAdjustment;
          }
        }
      }
    });

    setPrice({
      base: base,
      adjustments: totalAdjustments,
      total: base + totalAdjustments,
    });
  }, [template, options, selectedOptions, pricingRules, visibleOptions]);

  // Run logic on selection change
  useEffect(() => {
    evaluateRules();
  }, [selectedOptions, evaluateRules]);

  useEffect(() => {
    calculatePrice();
  }, [selectedOptions, visibleOptions, calculatePrice]);

  const selectOption = (groupId, optionId) => {
    isAutoCorrecting.current = false;
    setSelectedOptions((prev) => ({
      ...prev,
      [groupId]: optionId,
    }));
  };

  return {
    loading,
    template,
    optionGroups,
    options,
    selectedOptions,
    visibleOptions,
    disabledOptions,
    price,
    selectOption,
    productDetails: template?.product,
  };
};

export default useConfiguratorRuleEngine;
